import { useCallback, useRef, useState } from 'react';
import { useVisualizerStore } from '@/stores/useVisualizerStore';
import {
  requestUploadUrl,
  uploadToPresignedUrl,
  submitPreviewJob,
  checkPreviewStatus,
} from '@/api/preview';
import { submitLeadCapture } from '@/api/lead';
import { getFeatureFlags } from '@/utils/featureFlags';
import { detectDeviceCapabilities } from '@/utils/deviceTier';
import { compressForUpload } from '@/utils/compression';
import { trackEvent } from '@/api/analytics';
import { showToast } from '@/components/Toasts';
import type { PreviewStatusResponse } from '@/api/preview';

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 180; // ~6 minutes (outlives backend 5-min timeout)

export function usePreviewJob() {
  const setAppState = useVisualizerStore((s) => s.setAppState);
  const setProcessingProgress = useVisualizerStore((s) => s.setProcessingProgress);
  const setError = useVisualizerStore((s) => s.setError);
  const setPreviewResult = useVisualizerStore((s) => s.setPreviewResult);
  const setBeforeImagePublicUrl = useVisualizerStore((s) => s.setBeforeImagePublicUrl);
  const uploadedImage = useVisualizerStore((s) => s.uploadedImage);
  const selectedFinish = useVisualizerStore((s) => s.selectedFinish);
  const selectedGloss = useVisualizerStore((s) => s.selectedGloss);
  const refinishSurround = useVisualizerStore((s) => s.refinishSurround);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentJobIdRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    currentJobIdRef.current = null;
    setIsSubmitting(false);
  }, []);

  const submit = useCallback(async () => {
    if (!uploadedImage) return;

    // Cancel any in-flight job
    cancel();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsSubmitting(true);

    const imageId = uploadedImage.id;
    const flags = getFeatureFlags();

    // Detect device tier for strategy selection
    const deviceCaps = detectDeviceCapabilities();
    const deviceTier = deviceCaps.tier;

    try {
      setAppState('SUBMITTING_JOB');
      setProcessingProgress(0.1, 'Uploading photo...');
      trackEvent('preview_job_started', { deviceTier });

      // Fetch the preprocessed (oriented/resized) image blob
      const preprocessedRes = await fetch(uploadedImage.sourceUrl, {
        signal: abortController.signal,
      });
      const preprocessedBlob = await preprocessedRes.blob();
      const preprocessedFile = new File(
        [preprocessedBlob],
        uploadedImage.file.name.replace(/\.[^.]+$/, '.jpg'),
        { type: 'image/jpeg' }
      );

      const uploadFile =
        deviceTier === 'D' || deviceTier === 'C'
          ? await compressForUpload(preprocessedFile)
          : preprocessedFile;

      if (abortController.signal.aborted) return;

      const { imageId: serverImageId, uploadUrl, publicUrl } = await requestUploadUrl(
        uploadFile.name,
        uploadFile.type,
        uploadFile.size,
        abortController.signal
      );

      if (abortController.signal.aborted) return;

      setBeforeImagePublicUrl(publicUrl);

      const userEmail = useVisualizerStore.getState().userEmail;
      if (userEmail) {
        submitLeadCapture({
          email: userEmail,
          beforeImageUrl: publicUrl,
          finishId: selectedFinish.id,
          glossLevel: selectedGloss,
        }).catch(() => {});
      }

      setProcessingProgress(0.2, 'Preparing AI preview...');
      await uploadToPresignedUrl(uploadUrl, uploadFile, abortController.signal);

      if (abortController.signal.aborted) return;

      setAppState('PROCESSING_ON_SERVER');
      setProcessingProgress(0.3, 'AI is crafting your preview...');

      const previewRes = await submitPreviewJob(
        {
          imageId: serverImageId,
          imageUrl: publicUrl,
          finishId: selectedFinish.id,
          glossLevel: selectedGloss,
          refinishSurround,
          strategy: flags.renderStrategy,
          sessionId: imageId,
          deviceTier,
        },
        abortController.signal
      );

      if (abortController.signal.aborted) return;

      currentJobIdRef.current = previewRes.jobId;

      // Step 4: Poll for result
      const result = await pollUntilComplete(
        previewRes.jobId,
        abortController.signal,
        (status) => {
          // Reject results from old jobs if a new upload has replaced this one
          if (useVisualizerStore.getState().uploadedImage?.id !== imageId) {
            throw new Error('Cancelled');
          }

          const progress =
            status.status === 'queued'
              ? 0.35
              : status.status === 'submitted_to_bria'
                ? 0.5
                : status.status === 'bria_processing'
                  ? 0.75
                  : status.progress;

          const message =
            status.status === 'queued'
              ? 'Waiting in queue...'
              : status.status === 'submitted_to_bria'
                ? 'Processing your image...'
                : status.status === 'bria_processing'
                  ? 'AI is rendering your finish...'
                  : 'Almost done...';

          setProcessingProgress(progress, message);
        }
      );

      if (abortController.signal.aborted) return;

      // Only apply result if this is still the current job
      if (currentJobIdRef.current !== previewRes.jobId) return;

      if (result.status === 'completed') {
        if (result.resultUrl) {
          setPreviewResult(result.resultUrl);
          setAppState('PREVIEW');
          trackEvent('preview_job_completed', {
            finishId: selectedFinish.id,
            glossLevel: selectedGloss,
            refinishSurround,
            strategy: previewRes.strategy || flags.renderStrategy,
            deviceTier,
          });
          showToast('Preview ready!', 'success');
        } else {
          // Safeguard: backend returned completed but without resultUrl
          // This can happen during race conditions; do one immediate retry
          console.warn('[PreviewJob] Completed without resultUrl, retrying status check...');
          const retryStatus = await checkPreviewStatus(previewRes.jobId, abortController.signal);
          if (retryStatus.status === 'completed' && retryStatus.resultUrl) {
            setPreviewResult(retryStatus.resultUrl);
            setAppState('PREVIEW');
            trackEvent('preview_job_completed', {
              finishId: selectedFinish.id,
              glossLevel: selectedGloss,
              refinishSurround,
              strategy: previewRes.strategy || flags.renderStrategy,
              deviceTier,
            });
            showToast('Preview ready!', 'success');
          } else {
            console.error('[PreviewJob] Retry also failed:', retryStatus);
            throw new Error('Preview completed but result URL is missing. Please try again.');
          }
        }
      } else if (result.status === 'error') {
        throw new Error(result.error || 'AI processing failed');
      } else {
        throw new Error('Job did not complete successfully');
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      if (err instanceof Error && err.message.includes('Cancelled')) {
        return;
      }

      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[PreviewJob] Error:', err);

      if (message.includes('rate limit') || message.includes('429')) {
        setError({
          category: 'RATE_LIMITED',
          message: 'Too many requests. Please wait a moment and try again.',
          recoverable: true,
          retryAction: () => {
            setError(null);
            // Use a fresh submit call via store to avoid stale closure
            const state = useVisualizerStore.getState();
            state.setAppState('IDLE');
          },
        });
      } else {
        setError({
          category: 'UNKNOWN',
          message: message || 'Something went wrong with the AI preview. Please try again.',
          recoverable: true,
          retryAction: () => {
            setError(null);
            const state = useVisualizerStore.getState();
            state.setAppState('IDLE');
          },
        });
      }

      trackEvent('preview_job_failed', { error: message, deviceTier });
    } finally {
      setIsSubmitting(false);
      abortControllerRef.current = null;
    }
  }, [
    uploadedImage,
    selectedFinish,
    selectedGloss,
    refinishSurround,
    setAppState,
    setProcessingProgress,
    setError,
    setPreviewResult,
    setBeforeImagePublicUrl,
    cancel,
  ]);

  return { submit, cancel, isSubmitting };
}

async function pollUntilComplete(
  jobId: string,
  signal: AbortSignal,
  onUpdate?: (status: PreviewStatusResponse) => void
): Promise<PreviewStatusResponse> {
  for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
    if (signal.aborted) {
      const e = new Error('Aborted');
      e.name = 'AbortError';
      throw e;
    }

    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

    if (signal.aborted) {
      const e = new Error('Aborted');
      e.name = 'AbortError';
      throw e;
    }

    const status = await checkPreviewStatus(jobId, signal);
    onUpdate?.(status);

    if (status.status === 'completed' || status.status === 'error') {
      return status;
    }
  }

  throw new Error('Job timed out');
}
